/**
 * Modale "Processo di Firma XFIR" — PIN con timeout 60s .
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TIMEOUT_SEC = 59;

export function FirPinSignDialog({
  open,
  onOpenChange,
  firNumero,
  numIscrSito,
  onSigned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firNumero: string;
  numIscrSito?: string;
  onSigned?: () => void;
}) {
  const [pin, setPin] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SEC);

  useEffect(() => {
    if (!open) {
      setPin("");
      setSecondsLeft(TIMEOUT_SEC);
      return;
    }
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          toast.error("Tempo scaduto per la firma");
          onOpenChange(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, onOpenChange]);

  const handleSign = () => {
    if (pin.length < 4) {
      toast.error("Inserisci il PIN di firma");
      return;
    }
    // Demo: la firma reale richiede integrazione CA RENTRI / portale.
    toast.success(`Firma registrata per ${firNumero}`, {
      description: numIscrSito
        ? `Unità locale ${numIscrSito}`
        : "Verifica lo stato in Formulari digitali",
    });
    onSigned?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Processo di Firma XFIR</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Per firmare il formulario digitale inserisci il PIN di firma per questa
            unità locale.
          </DialogDescription>
          <p className="text-xs font-mono text-muted-foreground">{firNumero}</p>
        </DialogHeader>

        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
          Hai {secondsLeft} secondi per concludere l&apos;operazione
        </p>

        <label className="block space-y-1">
          <span className="text-xs font-medium">PIN</span>
          <Input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoComplete="off"
            maxLength={16}
          />
        </label>

        <button
          type="button"
          className="text-xs text-primary underline"
          onClick={() =>
            toast.info("Recupero PIN", {
              description: "Usa il portale RENTRI o la CA di riferimento.",
            })
          }
        >
          Hai dimenticato il PIN di firma?
        </button>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={handleSign} disabled={!pin.trim() || secondsLeft <= 0}>
            Firma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
